// src/config/payrollConfig.js
// BUG-FE-006 FIX: Centralized payroll configuration to replace hardcoded values

/**
 * Default Payroll Configuration
 * These values can be overridden by company settings from the backend
 */
export const PAYROLL_CONFIG = {
    // Working Days Configuration
    STANDARD_WORKING_DAYS_PER_MONTH: 22,
    STANDARD_WORKING_HOURS_PER_DAY: 8,
    HALF_DAY_HOURS_THRESHOLD: 4,

    // Deduction Rates (as decimals)
    PROVIDENT_FUND_RATE: 0.12, // 12% of basic salary
    EMPLOYEE_STATE_INSURANCE_RATE: 0.0075, // 0.75% (if applicable)

    // Fixed Deductions (monthly)
    PROFESSIONAL_TAX: 200, // ₹200 per month (varies by state)

    // Salary Calculation Defaults
    DEFAULT_BASIC_SALARY_PERCENTAGE: 0.50, // 50% of CTC
    DEFAULT_HRA_PERCENTAGE: 0.40, // 40% of basic (0.40 * 0.50 = 20% of CTC)
    DEFAULT_SPECIAL_ALLOWANCE_PERCENTAGE: 0.30, // 30% of CTC

    // ESI and PF Limits
    ESI_WAGE_LIMIT: 21000, // Monthly wage limit for ESI applicability
    PF_WAGE_CEILING: 15000, // Maximum basic salary for PF calculation

    // Tax Configuration
    TDS_THRESHOLD: 250000, // Annual income threshold for TDS (₹2.5 lakhs)

    // Proration Configuration
    PRORATION_BASE_DAYS: 22, // Base days for proration calculation

    // Validation Thresholds
    MINIMUM_CTC_WARNING: 100000, // Warn if CTC is below ₹1 lakh annually
    MAXIMUM_LOP_DAYS_WARNING: 10, // Warn if LOP exceeds 10 days

    // Processing Configuration
    PAYROLL_PROCESSING_TIMEOUT: 120000, // 2 minutes timeout for payroll processing
    MAX_CONCURRENT_PROCESSING: 1, // Only allow 1 concurrent payroll processing

    // Lock Configuration
    ALLOW_UNLOCK_WITHIN_HOURS: 24, // Allow unlock within 24 hours of locking
    REQUIRE_APPROVAL_FOR_UNLOCK: true,
};

/**
 * Payroll Status Constants
 */
export const PAYROLL_STATUS = {
    DRAFT: 'draft',
    VALIDATION_PENDING: 'validation_pending',
    VALIDATED: 'validated',
    PROCESSING: 'processing',
    COMPLETED: 'completed',
    LOCKED: 'locked',
    FAILED: 'failed',
};

/**
 * Validation Category Status
 */
export const VALIDATION_STATUS = {
    VALIDATED: 'validated',
    PENDING: 'pending',
    ISSUE: 'issue',
};

/**
 * Employee Status for Payroll
 */
export const EMPLOYEE_PAYROLL_STATUS = {
    ACTIVE: 'active',
    ON_HOLD: 'on_hold',
    EXITED: 'exited',
    NEW_JOINER: 'new_joiner',
};

/**
 * Get company-specific payroll configuration
 * This function should fetch from backend in production
 */
export const getPayrollConfig = async () => {
    try {
        // TODO: Fetch from backend API
        // const response = await api.get('/api/admin/payroll/configuration');
        // return { ...PAYROLL_CONFIG, ...response.data };

        // For now, return default config
        return PAYROLL_CONFIG;
    } catch (error) {
        console.warn('Failed to fetch payroll config, using defaults:', error);
        return PAYROLL_CONFIG;
    }
};

/**
 * Utility function to calculate PF based on configuration
 * CRITICAL FIX: Added input validation to prevent NaN propagation
 */
export const calculatePF = (basicSalary, config = PAYROLL_CONFIG) => {
    // Validate input is a finite number
    if (typeof basicSalary !== 'number' || !isFinite(basicSalary)) {
        console.error(`Invalid basicSalary for PF calculation: ${basicSalary}`);
        return 0;
    }

    // Validate basicSalary is non-negative
    if (basicSalary < 0) {
        console.warn(`Negative basicSalary for PF calculation: ${basicSalary}, using 0`);
        return 0;
    }

    // Validate config
    if (!config?.PF_WAGE_CEILING || !config?.PROVIDENT_FUND_RATE) {
        console.error('Invalid payroll configuration for PF calculation');
        return 0;
    }

    const applicableBasic = Math.min(basicSalary, config.PF_WAGE_CEILING);
    const pf = applicableBasic * config.PROVIDENT_FUND_RATE;

    // Round to 2 decimal places
    return Math.round(pf * 100) / 100;
};

/**
 * Utility function to calculate ESI based on configuration
 * CRITICAL FIX: Added input validation and clarified edge case handling
 */
export const calculateESI = (grossSalary, config = PAYROLL_CONFIG) => {
    // Validate input is a finite number
    if (typeof grossSalary !== 'number' || !isFinite(grossSalary)) {
        console.error(`Invalid grossSalary for ESI calculation: ${grossSalary}`);
        return 0;
    }

    // Validate grossSalary is non-negative
    if (grossSalary < 0) {
        console.warn(`Negative grossSalary for ESI calculation: ${grossSalary}, using 0`);
        return 0;
    }

    // Validate config
    if (!config?.ESI_WAGE_LIMIT || !config?.EMPLOYEE_STATE_INSURANCE_RATE) {
        console.error('Invalid payroll configuration for ESI calculation');
        return 0;
    }

    // ESI is not applicable if gross salary exceeds the wage limit
    if (grossSalary > config.ESI_WAGE_LIMIT) {
        return 0;
    }

    const esi = grossSalary * config.EMPLOYEE_STATE_INSURANCE_RATE;

    // Round to 2 decimal places
    return Math.round(esi * 100) / 100;
};

/**
 * State-wise Professional Tax Slabs (Monthly)
 * CRITICAL FIX: Implemented accurate PT slabs for major Indian states
 * Source: Latest state government notifications as of 2024-2025
 */
export const PT_SLABS = {
    // Maharashtra PT Slabs (Monthly)
    MAHARASHTRA: [
        { min: 0, max: 7500, tax: 0 },
        { min: 7501, max: 10000, tax: 175 },
        { min: 10001, max: Infinity, tax: 200 }
    ],

    // Karnataka PT Slabs (Monthly)
    KARNATAKA: [
        { min: 0, max: 15000, tax: 0 },
        { min: 15001, max: Infinity, tax: 200 }
    ],

    // West Bengal PT Slabs (Monthly)
    'WEST BENGAL': [
        { min: 0, max: 10000, tax: 0 },
        { min: 10001, max: 15000, tax: 110 },
        { min: 15001, max: 25000, tax: 130 },
        { min: 25001, max: 40000, tax: 150 },
        { min: 40001, max: Infinity, tax: 200 }
    ],

    // Tamil Nadu PT Slabs (Monthly)
    'TAMIL NADU': [
        { min: 0, max: 21000, tax: 0 },
        { min: 21001, max: Infinity, tax: 208.33 } // ₹2500 annually / 12
    ],

    // Gujarat PT Slabs (Monthly)
    GUJARAT: [
        { min: 0, max: 5999, tax: 0 },
        { min: 6000, max: 8999, tax: 80 },
        { min: 9000, max: 11999, tax: 150 },
        { min: 12000, max: Infinity, tax: 200 }
    ],

    // Andhra Pradesh PT Slabs (Monthly)
    'ANDHRA PRADESH': [
        { min: 0, max: 15000, tax: 0 },
        { min: 15001, max: 20000, tax: 150 },
        { min: 20001, max: Infinity, tax: 200 }
    ],

    // Telangana PT Slabs (Monthly)
    TELANGANA: [
        { min: 0, max: 15000, tax: 0 },
        { min: 15001, max: 20000, tax: 150 },
        { min: 20001, max: Infinity, tax: 200 }
    ],

    // Madhya Pradesh PT Slabs (Monthly)
    'MADHYA PRADESH': [
        { min: 0, max: 15000, tax: 0 },
        { min: 15001, max: Infinity, tax: 208.33 } // ₹2500 annually / 12
    ],

    // Chhattisgarh PT Slabs (Monthly)
    CHHATTISGARH: [
        { min: 0, max: 12500, tax: 0 },
        { min: 12501, max: 16666, tax: 150 },
        { min: 16667, max: Infinity, tax: 208.33 } // ₹2500 annually / 12
    ],

    // Odisha PT Slabs (Monthly)
    ODISHA: [
        { min: 0, max: 7083, tax: 0 },
        { min: 7084, max: 10416, tax: 100 },
        { min: 10417, max: 13750, tax: 150 },
        { min: 13751, max: Infinity, tax: 200 }
    ],

    // Assam PT Slabs (Monthly)
    ASSAM: [
        { min: 0, max: 10000, tax: 0 },
        { min: 10001, max: 15000, tax: 150 },
        { min: 15001, max: 25000, tax: 180 },
        { min: 25001, max: Infinity, tax: 208 }
    ],

    // Jharkhand PT Slabs (Monthly)
    JHARKHAND: [
        { min: 0, max: 15000, tax: 0 },
        { min: 15001, max: 20000, tax: 150 },
        { min: 20001, max: Infinity, tax: 200 }
    ],

    // Meghalaya PT Slabs (Monthly)
    MEGHALAYA: [
        { min: 0, max: 4166, tax: 0 },
        { min: 4167, max: 6250, tax: 41.67 },
        { min: 6251, max: 8333, tax: 83.33 },
        { min: 8334, max: Infinity, tax: 166.67 }
    ],

    // DEFAULT: For states without PT or unknown states
    DEFAULT: [
        { min: 0, max: Infinity, tax: 0 }
    ]
};

/**
 * Utility function to get professional tax based on state and salary
 * CRITICAL FIX: Implemented accurate state-wise PT calculation with salary slabs
 *
 * @param {string} state - State name (case-insensitive)
 * @param {number} grossSalary - Monthly gross salary
 * @param {object} config - Payroll configuration (for backwards compatibility)
 * @returns {number} Professional tax amount (rounded to 2 decimals)
 */
export const getProfessionalTax = (state = 'default', grossSalary = 0, config = PAYROLL_CONFIG) => {
    // Validate grossSalary is a number
    if (typeof grossSalary !== 'number' || !isFinite(grossSalary)) {
        console.error(`Invalid grossSalary for PT calculation: ${grossSalary}`);
        return 0;
    }

    // Validate grossSalary is non-negative
    if (grossSalary < 0) {
        console.warn(`Negative grossSalary for PT calculation: ${grossSalary}, using 0`);
        return 0;
    }

    // Normalize state name to uppercase for comparison
    const normalizedState = (state || 'DEFAULT').toUpperCase().trim();

    // Get PT slabs for the state (default to DEFAULT if not found)
    const statePTSlabs = PT_SLABS[normalizedState] || PT_SLABS.DEFAULT;

    // Find the applicable slab
    const applicableSlab = statePTSlabs.find(slab =>
        grossSalary >= slab.min && grossSalary <= slab.max
    );

    // If no slab found (shouldn't happen with proper config), return 0
    if (!applicableSlab) {
        console.warn(`No PT slab found for state: ${state}, salary: ${grossSalary}`);
        return 0;
    }

    // Round to 2 decimal places
    return Math.round(applicableSlab.tax * 100) / 100;
};

export default PAYROLL_CONFIG;
